
import React, { useEffect } from 'react';
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Color from '@tiptap/extension-color';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Heading1,
  Heading2,
  Heading3,
  Code,
  Quote,
  Undo,
  Redo,
  X,
  Table as TableIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import '@/styles/editor.css';

interface WysiwygEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export const WysiwygEditor = ({
  value,
  onChange,
  placeholder = 'Write something...',
}: WysiwygEditorProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: false,
      }),
      Placeholder.configure({
        placeholder,
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Color,
      Table.configure({
        resizable: true,
      }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html);
    },
    // Add editor classes to match the public blog styling
    editorProps: {
      attributes: {
        class: 'prose prose-lg max-w-none p-4 min-h-[400px] focus:outline-none tiptap-content',
      },
    },
  });

  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      if (editor.getHTML() !== value) {
        editor.commands.setContent(value);
      }
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  const addImage = () => {
    const url = prompt('URL');
    if (url) {
      // Using the correct method for inserting an image
      editor.chain().focus().insertContent({
        type: 'image',
        attrs: { src: url }
      }).run();
    }
  };

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = prompt('URL', previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    // update link
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const addTable = () => {
    // Make sure we're using the correct table insertion command
    editor.chain()
      .focus()
      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
      .run();
  };

  return (
    <div className="border rounded-md">
      <div className="bg-white p-2 border-b flex flex-wrap items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 ${editor.isActive('bold') ? 'bg-slate-200' : ''}`}
          type="button"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 ${editor.isActive('italic') ? 'bg-slate-200' : ''}`}
          type="button"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleMark('underline').run()}
          className={`p-2 ${editor.isActive('underline') ? 'bg-slate-200' : ''}`}
          type="button"
        >
          <UnderlineIcon className="h-4 w-4" />
        </Button>

        <span className="w-px h-6 bg-gray-300 mx-1"></span>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`p-2 ${
            editor.isActive('heading', { level: 1 }) ? 'bg-slate-200' : ''
          }`}
          type="button"
        >
          <Heading1 className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`p-2 ${
            editor.isActive('heading', { level: 2 }) ? 'bg-slate-200' : ''
          }`}
          type="button"
        >
          <Heading2 className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`p-2 ${
            editor.isActive('heading', { level: 3 }) ? 'bg-slate-200' : ''
          }`}
          type="button"
        >
          <Heading3 className="h-4 w-4" />
        </Button>

        <span className="w-px h-6 bg-gray-300 mx-1"></span>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 ${editor.isActive('bulletList') ? 'bg-slate-200' : ''}`}
          type="button"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 ${
            editor.isActive('orderedList') ? 'bg-slate-200' : ''
          }`}
          type="button"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>

        <span className="w-px h-6 bg-gray-300 mx-1"></span>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.commands.setTextAlign('left')}
          className={`p-2 ${
            editor.isActive({ textAlign: 'left' }) ? 'bg-slate-200' : ''
          }`}
          type="button"
        >
          <AlignLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.commands.setTextAlign('center')}
          className={`p-2 ${
            editor.isActive({ textAlign: 'center' }) ? 'bg-slate-200' : ''
          }`}
          type="button"
        >
          <AlignCenter className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.commands.setTextAlign('right')}
          className={`p-2 ${
            editor.isActive({ textAlign: 'right' }) ? 'bg-slate-200' : ''
          }`}
          type="button"
        >
          <AlignRight className="h-4 w-4" />
        </Button>

        <span className="w-px h-6 bg-gray-300 mx-1"></span>

        <Button
          variant="ghost"
          size="sm"
          onClick={setLink}
          className={`p-2 ${editor.isActive('link') ? 'bg-slate-200' : ''}`}
          type="button"
        >
          <LinkIcon className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={addImage}
          className="p-2"
          type="button"
        >
          <ImageIcon className="h-4 w-4" />
        </Button>

        <span className="w-px h-6 bg-gray-300 mx-1"></span>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          className={`p-2 ${editor.isActive('codeBlock') ? 'bg-slate-200' : ''}`}
          type="button"
        >
          <Code className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          className={`p-2 ${editor.isActive('blockquote') ? 'bg-slate-200' : ''}`}
          type="button"
        >
          <Quote className="h-4 w-4" />
        </Button>

        <span className="w-px h-6 bg-gray-300 mx-1"></span>

        <Button
          variant="ghost"
          size="sm"
          onClick={addTable}
          className="p-2"
          type="button"
        >
          <TableIcon className="h-4 w-4" />
        </Button>

        <span className="w-px h-6 bg-gray-300 mx-1"></span>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          className="p-2"
          type="button"
        >
          <Undo className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          className="p-2"
          type="button"
        >
          <Redo className="h-4 w-4" />
        </Button>
      </div>

      {editor && (
        <BubbleMenu
          editor={editor}
          tippyOptions={{ duration: 100 }}
          className="bg-white rounded-md shadow-md p-1 flex gap-1 border"
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`p-1 ${editor.isActive('bold') ? 'bg-slate-200' : ''}`}
          >
            <Bold className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`p-1 ${editor.isActive('italic') ? 'bg-slate-200' : ''}`}
          >
            <Italic className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleMark('underline').run()}
            className={`p-1 ${
              editor.isActive('underline') ? 'bg-slate-200' : ''
            }`}
          >
            <UnderlineIcon className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={setLink}
            className={`p-1 ${editor.isActive('link') ? 'bg-slate-200' : ''}`}
          >
            <LinkIcon className="h-3 w-3" />
          </Button>

          {editor.isActive('link') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().unsetLink().run()}
              className="p-1 text-red-500"
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </BubbleMenu>
      )}

      <EditorContent
        editor={editor}
        className="prose prose-lg max-w-none p-4 min-h-[400px] focus:outline-none tiptap-content"
      />
    </div>
  );
};
